/*
 * Artich — Champ code promo du panier (tiroir et page panier).
 * Markup : snippets/artich-cart-discount.liquid
 *
 * Se branche sur la couche panier du thème sans la modifier :
 * - route et en-têtes : theme.routes.cart_update_url et fetchConfig() (global.js) ;
 * - sections à rafraîchir : celles que déclare <cart-items> (cart.js) ;
 * - après mise à jour : événement cart:updated et PUB_SUB_EVENTS.cartUpdate, comme cart.js.
 */
if (!customElements.get('artich-cart-discount')) {
  class ArtichCartDiscount extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('[data-artich-discount-input]');
      this.applyButton = this.querySelector('[data-artich-discount-apply]');
      this.message = this.querySelector('[data-artich-discount-message]');

      this.addEventListener('click', this.onClick);
      this.input?.addEventListener('keydown', this.onKeydown);
      this.input?.addEventListener('input', this.onInput);
      document.addEventListener('cart:updated', this.onCartUpdated);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      this.input?.removeEventListener('keydown', this.onKeydown);
      this.input?.removeEventListener('input', this.onInput);
      document.removeEventListener('cart:updated', this.onCartUpdated);
    }

    get codes() {
      return (this.dataset.codes || '').split(',').map((code) => code.trim()).filter(Boolean);
    }

    onClick = (event) => {
      if (event.target.closest('[data-artich-discount-apply]')) {
        this.applyCode();
        return;
      }

      const removeButton = event.target.closest('[data-artich-discount-remove]');
      if (removeButton) this.removeCode(removeButton.dataset.artichDiscountRemove);
    };

    onKeydown = (event) => {
      // Le tiroir est un <form> : sans ça, Entrée enverrait le client au checkout
      if (event.key !== 'Enter') return;
      event.preventDefault();
      this.applyCode();
    };

    onInput = () => {
      if (this.input.hasAttribute('aria-invalid')) this.showMessage('');
    };

    // Sur la page panier, cart.js ne rafraîchit pas ce bloc après un changement de quantité :
    // on reprend son HTML dans la réponse déjà reçue, sans nouvelle requête.
    onCartUpdated = (event) => {
      if (this.busy) return;
      try {
        this.replaceWithFresh(JSON.parse(event.detail?.cart || '{}'));
      } catch (error) {
        // Réponse inattendue : on garde l'affichage actuel
      }
    };

    async applyCode() {
      if (this.busy) return;
      const code = this.input?.value.trim();
      if (!code) {
        this.showMessage(this.dataset.errorEmpty, 'error');
        return;
      }

      const current = this.codes;
      if (current.some((existing) => existing.toLowerCase() === code.toLowerCase())) {
        this.showMessage(this.dataset.errorDuplicate, 'error');
        return;
      }

      const state = await this.update([...current, code]);
      if (!state) return;

      const result = state.discount_codes?.find((discount) => discount.code.toLowerCase() === code.toLowerCase());
      if (!result?.applicable) {
        // Code inconnu ou non applicable : on le retire pour ne pas l'emporter jusqu'au checkout.
        // Le panier revient à son état précédent, inutile de le re-rendre.
        if (await this.update(current, false)) this.showMessage(this.dataset.errorInvalid, 'error').focusInput();
        return;
      }

      this.render(state)?.showMessage(this.dataset.messageApplied, 'success').focusInput();
    }

    async removeCode(code) {
      if (this.busy) return;
      const state = await this.update(this.codes.filter((existing) => existing !== code));
      if (state) this.render(state)?.showMessage(this.dataset.messageRemoved, 'success').focusInput();
    }

    async update(codes, withSections = true) {
      this.setBusy(true);
      try {
        const body = { discount: codes.join(',') };
        if (withSections) {
          body.sections = this.sectionIds();
          body.sections_url = window.location.pathname;
        }

        const response = await fetch(theme.routes.cart_update_url, { ...fetchConfig(), body: JSON.stringify(body) });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return await response.json();
      } catch (error) {
        this.showMessage(this.dataset.errorGeneric, 'error');
        return null;
      } finally {
        this.setBusy(false);
      }
    }

    // Même liste que cart.js, pour que lignes, totaux et pastille du panier restent synchronisés
    sectionsToRender() {
      const cartItems = document.querySelector('cart-items');
      const sections = typeof cartItems?.getSectionsToRender === 'function' ? cartItems.getSectionsToRender() : [];
      return sections.filter((section) => section.section);
    }

    sectionIds() {
      return [...new Set([...this.sectionsToRender().map((section) => section.section), this.dataset.sectionId])];
    }

    render(state) {
      const sectionId = this.dataset.sectionId;

      this.sectionsToRender().forEach((section) => {
        const element = document.getElementById(section.id);
        const html = state.sections?.[section.section];
        if (!element || !html) return;

        const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector(section.selector);
        if (fresh) (element.querySelector(section.selector) || element).innerHTML = fresh.innerHTML;
      });

      // Page panier : ce bloc est hors des zones rafraîchies par cart.js
      this.replaceWithFresh(state);

      const cartJson = JSON.stringify(state);
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cartJson } }));
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'artich-cart-discount' });
      }

      return document.querySelector(`artich-cart-discount[data-section-id="${CSS.escape(sectionId)}"]`);
    }

    replaceWithFresh(state) {
      const html = state.sections?.[this.dataset.sectionId];
      if (!html || !this.isConnected) return;

      const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('artich-cart-discount');
      if (!fresh) {
        this.remove();
      } else if (fresh.outerHTML !== this.restingHTML()) {
        // Comparaison pour ne pas effacer une saisie ou un message quand rien n'a changé côté serveur
        this.replaceWith(fresh);
      }
    }

    // HTML de ce bloc sans les états posés par le JS, comparable au rendu serveur
    restingHTML() {
      const clone = this.cloneNode(true);
      const message = clone.querySelector('[data-artich-discount-message]');
      clone.removeAttribute('aria-busy');
      message?.replaceChildren();
      message?.classList.remove('artich-cart-discount__message--error');
      clone.querySelector('[data-artich-discount-input]')?.removeAttribute('aria-invalid');
      return clone.outerHTML;
    }

    setBusy(busy) {
      this.busy = busy;
      this.setAttribute('aria-busy', String(busy));
      this.applyButton?.classList.toggle('loading', busy);
      this.querySelectorAll('button').forEach((button) => {
        button.disabled = busy;
      });
    }

    showMessage(text, type) {
      if (this.message) {
        this.message.textContent = text || '';
        this.message.classList.toggle('artich-cart-discount__message--error', type === 'error');
      }
      if (type === 'error') {
        this.input?.setAttribute('aria-invalid', 'true');
      } else {
        this.input?.removeAttribute('aria-invalid');
      }
      return this;
    }

    focusInput() {
      this.input?.focus();
      return this;
    }
  }

  customElements.define('artich-cart-discount', ArtichCartDiscount);
}
